import {
  ChangeDetectorRef,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  signal,
} from '@angular/core';
import {ButtonSize} from '../../internal';
import {Buttons} from './constants';

export type ButtonTypes =
  | Buttons.Action
  | Buttons.Secondary
  | Buttons.Warning
  | Buttons.Icon
  | Buttons.Link
  | Buttons.LinkInline
  | Buttons.Export
  | Buttons.TabLink
  | Buttons.SideTabLink
  | '';

type ButtonTypesLiteral = `${ButtonTypes}`;

type ButtonWidth = 'mini' | 'normal';

const BUTTON_TYPES_CLASS_MAPPING: Partial<Record<ButtonTypes, string>> = {
  [Buttons.Action]: 'action-button',
  [Buttons.Secondary]: 'secondary-button',
  [Buttons.Warning]: 'danger-button',
  [Buttons.Icon]: 'ico-button',
  [Buttons.Link]: 'link-button',
  [Buttons.LinkInline]: 'link-button--inline',
  [Buttons.Export]: 'ico-button',
  [Buttons.TabLink]: 'tab-link-button',
  [Buttons.SideTabLink]: 'side-tab-link-button',
};

const BUTTON_SIZES_CLASS_MAPPING: Partial<Record<ButtonSize, string>> = {
  large: 'functional',
};

const BUTTON_WIDTH_CLASS_MAPPING: Partial<Record<ButtonWidth, string>> = {
  mini: 'mini',
};

const resolveButtonTypeClass = (type: ButtonTypes): string | undefined => BUTTON_TYPES_CLASS_MAPPING[type];
const resolveButtonSizeClass = (size: ButtonSize): string | undefined => BUTTON_SIZES_CLASS_MAPPING[size];
const resolveButtonWidthClass = (width: ButtonWidth): string | undefined => BUTTON_WIDTH_CLASS_MAPPING[width];

@Directive({
  selector: 'button[msButton]',
  exportAs: 'msButton',
  standalone: true,
})
export class ButtonDirective {
  readonly msButton = input<ButtonTypes | ButtonTypesLiteral>('');
  readonly buttonType = input<ButtonTypes | ButtonTypesLiteral>('');
  readonly size = input<ButtonSize>('normal');
  readonly buttonWidth = input<ButtonWidth>('normal');

  private readonly renderer = inject(Renderer2);
  private readonly el = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  private currentType: ButtonTypes = '';
  private currentSize: ButtonSize = 'normal';
  private currentWidth: ButtonWidth = 'normal';

  private readonly panelSize = signal<ButtonSize | null>(null);

  private readonly resolvedType = computed(() => (this.buttonType() || this.msButton()) as ButtonTypes);
  readonly resolvedSize = computed(() => this.panelSize() ?? this.size());

  setPanelSize(size: ButtonSize | null): void {
    this.panelSize.set(size);
  }

  constructor() {
    this.initButtonTypeEffect();
    this.initButtonSizeEffect();
    this.initButtonWidthEffect();
  }

  private initButtonTypeEffect(): void {
    effect(() => {
      this.handleButtonTypeChange(this.resolvedType());
    });
  }

  private initButtonSizeEffect(): void {
    effect(() => {
      this.handleButtonSizeChange(this.resolvedSize());
    });
  }

  private initButtonWidthEffect(): void {
    effect(() => {
      this.handleButtonWidthChange(this.buttonWidth());
    });
  }

  private handleButtonTypeChange(type: ButtonTypes): void {
    this.replaceClass(resolveButtonTypeClass(this.currentType), resolveButtonTypeClass(type));
    this.currentType = type;
    this.cdRef.markForCheck();
  }

  private handleButtonSizeChange(size: ButtonSize): void {
    this.replaceClass(resolveButtonSizeClass(this.currentSize), resolveButtonSizeClass(size));
    this.currentSize = size;
    this.cdRef.markForCheck();
  }

  private handleButtonWidthChange(width: ButtonWidth): void {
    this.replaceClass(resolveButtonWidthClass(this.currentWidth), resolveButtonWidthClass(width));
    this.currentWidth = width;
  }

  private addClass(className?: string): void {
    if (className) {
      this.renderer.addClass(this.el.nativeElement, className);
    }
  }

  private removeClass(className?: string): void {
    if (className) {
      this.renderer.removeClass(this.el.nativeElement, className);
    }
  }

  private replaceClass(oldClass?: string, newClass?: string): void {
    this.removeClass(oldClass);
    this.addClass(newClass);
  }
}
